/**
 * PatternIQ Copilot Feedback Analytics
 * =====================================
 * Analytics dashboard with charts and metrics
 */

import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ThumbsUp, ThumbsDown, MessageSquare, Clock } from 'lucide-react';

export default function FeedbackAnalytics() {
  // Fetch analytics data with 5-second refetch for real-time updates
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['copilot-feedback-analytics'],
    queryFn: async () => {
      const response = await fetch('/api/copilot/feedback/analytics?days=30');
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch analytics: ${response.status} ${errorText}`);
      }
      const result = await response.json();
      return result.data;
    },
    refetchInterval: 5000,
    staleTime: 0,
    retry: 1, // Only retry once
  });

  // Check if data is stale (> 30 seconds old)
  const isStale = Date.now() - dataUpdatedAt > 30000;

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-red-600">Error loading analytics: {(error as Error).message}</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const { overview, trendData, functionPerformance, mostUsedFunctions } = data;

  return (
    <div className="space-y-6">
      {/* Data Freshness Indicator */}
      <div className="flex items-center gap-2 text-sm">
        <div className={`h-2 w-2 rounded-full ${isStale ? 'bg-red-500' : 'bg-green-500'}`}></div>
        <span className="text-gray-600">
          {isStale ? 'Data may be stale' : 'Live data'} • Last updated: {new Date(dataUpdatedAt).toLocaleTimeString()}
        </span>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.totalConversations.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Last 30 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Satisfaction Rate</CardTitle>
            <ThumbsUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview.satisfactionRate !== null ? `${overview.satisfactionRate}%` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {overview.thumbsUp} 👍 / {overview.thumbsDown} 👎
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Ratings</CardTitle>
            <ThumbsDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.totalRatings.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {overview.totalConversations > 0 
                ? `${((overview.totalRatings / overview.totalConversations) * 100).toFixed(0)}% rated`
                : 'No data'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview.avgResponseTime ? `${(overview.avgResponseTime / 1000).toFixed(1)}s` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">Function execution time</p>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      {trendData && trendData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Feedback Trend</CardTitle>
            <CardDescription>Daily feedback over the last 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: any) => [value, '']}
                />
                <Legend />
                <Line type="monotone" dataKey="thumbsUp" stroke="#22c55e" name="Thumbs Up" strokeWidth={2} />
                <Line type="monotone" dataKey="thumbsDown" stroke="#ef4444" name="Thumbs Down" strokeWidth={2} />
                <Line type="monotone" dataKey="total" stroke="#3b82f6" name="Total" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Function Performance Table */}
      {functionPerformance && functionPerformance.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Function Performance</CardTitle>
            <CardDescription>Satisfaction rate by function</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Function Name</th>
                    <th className="text-right py-2 px-2">Total Calls</th>
                    <th className="text-right py-2 px-2">👍</th>
                    <th className="text-right py-2 px-2">👎</th>
                    <th className="text-right py-2 px-2">Satisfaction</th>
                    <th className="text-right py-2 px-2">Avg Time (s)</th>
                  </tr>
                </thead>
                <tbody>
                  {functionPerformance.map((func: any) => (
                    <tr key={func.functionName} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2 font-mono text-xs">{func.functionName}</td>
                      <td className="text-right py-2 px-2">{func.totalCalls}</td>
                      <td className="text-right py-2 px-2 text-green-600">{func.thumbsUp}</td>
                      <td className="text-right py-2 px-2 text-red-600">{func.thumbsDown}</td>
                      <td className="text-right py-2 px-2">
                        {func.satisfactionRate !== null ? (
                          <span className={func.satisfactionRate >= 70 ? 'text-green-600 font-medium' : func.satisfactionRate >= 50 ? 'text-amber-600' : 'text-red-600 font-medium'}>
                            {func.satisfactionRate.toFixed(0)}%
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="text-right py-2 px-2">
                        {func.avgResponseTime ? (func.avgResponseTime / 1000).toFixed(2) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Most Used Functions */}
      {mostUsedFunctions && mostUsedFunctions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Most Used Functions</CardTitle>
            <CardDescription>Top 10 functions by usage count</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {mostUsedFunctions.map((func: any, index: number) => (
                <div key={func.functionName} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                    <span className="font-mono text-sm">{func.functionName}</span>
                  </div>
                  <span className="text-sm font-medium">{func.usageCount} calls</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

